import {
	galleryAtom,
	gallerySizeAtom,
	googleReviewsAtom,
	hasCurrentUserReviewedAtom,
	ratingAtom,
	reviewStarBucketsAtom,
	reviewsWithPhotosCountAtom,
	totalCountAtom,
} from '@components/reviews/atoms';
import {
	currentResultsCountAtom,
	reviewsCollectionAtom,
} from '@components/reviews/shared-atoms';
import { roundReviewRating } from '@decorators/vendor/helpers';
import {
	getAvailabilityDataToHydrate,
	getAvailabilityInitialValues,
} from '@pages/Storefront/components/AvailabilitySection/availability-atoms';
import { reviewSnippetsDataAtom } from '@pages/Storefront/components/ReviewSnippets/atoms';
import type { ReviewSnippetsData } from '@pages/Storefront/components/ReviewSnippets/types';
import type { StorefrontReviews } from '@typings/reviews';
import { largestRemainderMethod } from '@utils/largestRemainderMethod';
import { atom } from 'jotai';
import type { createStore } from 'jotai/vanilla';
import type {
	GoogleReviewsResponseData,
	ReviewsResponseData,
	ReviewsSummary,
} from 'types/api/reviews';
import { type FeatureFlags, featureFlagsAtom } from '../feature-flags';
import {
	getSeoExperimentDataToHydrate,
	getSeoExperimentInitialValues,
} from './shared/seo-experiment';
import {
	getAwardsSummaryDataToHydrate,
	getAwardsSummaryInitialValues,
} from './storefront/awards-summary';
import {
	getContentGatingDataToHydrate,
	getContentGatingInitialValues,
} from './storefront/content-gating';
import {
	getDetailedPricingDataToHydrate,
	getDetailedPricingInitialValues,
} from './storefront/detailed-pricing';
import {
	getHostedBadgeDataToHydrate,
	getHostedBadgeInitialValues,
} from './storefront/hosted-badge';
import {
	getOrganizationDataToHydrate,
	getOrganizationInitialValues,
} from './storefront/organization';
import {
	getPricingDataToHydrate,
	getPricingInitialValues,
} from './storefront/pricing';
import {
	getReviewPillsDataToHydrate,
	getReviewPillsInitialValues,
} from './storefront/review-pills';
import {
	getSemanticSearchDataToHydrate,
	getSemanticSearchInitialValues,
} from './storefront/semantic-search';
import {
	getStorefrontsNetworkDataToHydrate,
	getStorefrontsNetworkInitialValues,
} from './storefront/storefronts-network-pvrec';

import {
	type ClassifierSsrSync,
	type FilterConfigs,
	type FilterValue,
	baseFilterConfigsAtom,
	categoryFilterSummariesAtom,
	classifierSsrSyncAtom,
	filterConfigsAtom,
	filterValuesAtom,
	isAllCategoriesPageAtom,
	listingsPricingByCategoryAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import type { SearchFilters } from 'types/search';

const initialState: StorefrontReviews = {
	collection: [],
	isFetching: true,
	totalCount: 0,
	gallerySize: 0,
	rating: 0,
	totalCountWithReviews: 0,
	buckets: {
		stars1: 0,
		stars2: 0,
		stars3: 0,
		stars4: 0,
		stars5: 0,
	},
	gallery: [],
	reviewsWithPhotosCount: 0,
	hasCurrentUserReviewed: false,
};

export interface InitialJotaiState {
	reviews: StorefrontReviews;
	googleReviews: GoogleReviewsResponseData;
	pricing: ReturnType<typeof getPricingDataToHydrate>;
	featureFlags: FeatureFlags;
	detailedPricing: ReturnType<typeof getDetailedPricingDataToHydrate>;
	organization: ReturnType<typeof getOrganizationDataToHydrate>;
	semanticSearch: ReturnType<typeof getSemanticSearchDataToHydrate>;
	seoExperiment: ReturnType<typeof getSeoExperimentDataToHydrate>;
	contentGating: ReturnType<typeof getContentGatingDataToHydrate>;
	availability: ReturnType<typeof getAvailabilityDataToHydrate>;
	storefrontsNetworkPvRec: ReturnType<
		typeof getStorefrontsNetworkDataToHydrate
	>;
	awardsSummary: ReturnType<typeof getAwardsSummaryDataToHydrate>;
	reviewPills: ReturnType<typeof getReviewPillsDataToHydrate>;
	hostedBadge: ReturnType<typeof getHostedBadgeDataToHydrate>;
	filterValues: FilterValue[];
	filterConfigs: FilterConfigs;
	baseFilterConfigs: FilterConfigs | null;
	categoryFilterSummaries: Record<string, SearchFilters>;
	isAllCategoriesPage: boolean;
	listingsPricingByCategory: Record<string, boolean>;
	reviewSnippets: ReviewSnippetsData | null;
	classifierSsrSync: ClassifierSsrSync | null;
}

const reviewsAtom = atom<StorefrontReviews>(initialState);

export const setReviews = (
	store: ReturnType<typeof createStore>,
	reviewsResponse: ReviewsResponseData,
) => {
	const { data, totalCount, gallery, reviewsSummary, reviewsWithPhotosCount } =
		reviewsResponse;
	const hasCurrentUserReviewed = reviewsResponse.hasReviewed ?? false;
	const count = calculationDistributionCount(reviewsSummary);
	const reviews = {
		...initialState,
		collection: data,
		totalCount,
		gallerySize: reviewsSummary ? reviewsSummary.gallerySize : 0,
		totalCountWithReviews: count,
		reviewsWithPhotosCount: reviewsWithPhotosCount ?? 0,
		rating: reviewsSummary ? roundReviewRating(reviewsSummary.rating) : 0,
		buckets:
			reviewsSummary && count > 0
				? calculateDistribution(reviewsSummary, count)
				: initialState.buckets,
		gallery,
		hasCurrentUserReviewed,
	};
	store.set(reviewsAtom, reviews);
	store.set(hasCurrentUserReviewedAtom, hasCurrentUserReviewed);
};

function calculationDistributionCount(summary: ReviewsSummary | undefined) {
	if (!summary) return 0;
	const { distribution } = summary;
	return (
		distribution.stars1 +
		distribution.stars2 +
		distribution.stars3 +
		distribution.stars4 +
		distribution.stars5
	);
}

function calculateBucket(bucket: number, count: number) {
	return (bucket / count) * 100;
}

function calculateDistribution(
	{ distribution }: ReviewsSummary,
	count: number,
): StorefrontReviews['buckets'] {
	const buckets = largestRemainderMethod(
		[
			calculateBucket(distribution.stars5, count),
			calculateBucket(distribution.stars4, count),
			calculateBucket(distribution.stars3, count),
			calculateBucket(distribution.stars2, count),
			calculateBucket(distribution.stars1, count),
		],
		100,
	);
	return {
		stars1: buckets[4] / 100,
		stars2: buckets[3] / 100,
		stars3: buckets[2] / 100,
		stars4: buckets[1] / 100,
		stars5: buckets[0] / 100,
	};
}

const getReviewsInitialValues = (reviews: StorefrontReviews) => {
	if (!reviews) return [];
	const {
		buckets,
		collection,
		gallery,
		rating,
		totalCount,
		gallerySize,
		reviewsWithPhotosCount,
		hasCurrentUserReviewed,
	} = reviews;
	return [
		[reviewStarBucketsAtom, buckets],
		[reviewsCollectionAtom, collection],
		[gallerySizeAtom, gallerySize],
		[galleryAtom, gallery],
		[ratingAtom, rating],
		[totalCountAtom, totalCount],
		[currentResultsCountAtom, totalCount],
		[reviewsWithPhotosCountAtom, reviewsWithPhotosCount],
		[hasCurrentUserReviewedAtom, hasCurrentUserReviewed],
	];
};

const getFeatureFlagsInitialValues = (featureFlags: FeatureFlags) => {
	if (!featureFlags) return [];

	return [[featureFlagsAtom, featureFlags]];
};

const getGoogleReviewsInitialValues = (
	googleReviews: GoogleReviewsResponseData,
) => {
	return [[googleReviewsAtom, googleReviews]];
};

const getFilterValuesInitialValues = (filterValues: FilterValue[]) => {
	return [[filterValuesAtom, filterValues]];
};

const getFilterConfigsInitialValues = (filterConfigs: FilterConfigs) => {
	return [[filterConfigsAtom, filterConfigs]];
};

const getBaseFilterConfigsInitialValues = (
	baseFilterConfigs: FilterConfigs | null,
) => {
	return [[baseFilterConfigsAtom, baseFilterConfigs]];
};

const getCategoryFilterSummariesInitialValues = (
	categoryFilterSummaries: InitialJotaiState['categoryFilterSummaries'],
) => {
	return [[categoryFilterSummariesAtom, categoryFilterSummaries ?? {}]];
};

const getIsAllCategoriesPageInitialValues = (isAllCategoriesPage: boolean) => {
	return [[isAllCategoriesPageAtom, isAllCategoriesPage]];
};

const getListingsPricingByCategoryInitialValues = (
	listingsPricingByCategory: Record<string, boolean>,
) => {
	return [[listingsPricingByCategoryAtom, listingsPricingByCategory]];
};

const getClassifierSsrSyncInitialValues = (
	classifierSsrSync: ClassifierSsrSync | null,
) => {
	return [[classifierSsrSyncAtom, classifierSsrSync]];
};

export const getJotaiDataToHydrate = (
	store: ReturnType<typeof createStore>,
) => {
	const reviews = store.get(reviewsAtom);
	const googleReviews = store.get(googleReviewsAtom);
	const pricing = getPricingDataToHydrate(store);
	const featureFlags = store.get(featureFlagsAtom);
	const detailedPricing = getDetailedPricingDataToHydrate(store);
	const organization = getOrganizationDataToHydrate(store);
	const semanticSearch = getSemanticSearchDataToHydrate(store);
	const seoExperiment = getSeoExperimentDataToHydrate(store);
	const contentGating = getContentGatingDataToHydrate(store);
	const availability = getAvailabilityDataToHydrate(store);
	const storefrontsNetworkPvRec = getStorefrontsNetworkDataToHydrate(store);
	const awardsSummary = getAwardsSummaryDataToHydrate(store);
	const reviewPills = getReviewPillsDataToHydrate(store);
	const hostedBadge = getHostedBadgeDataToHydrate(store);
	const reviewSnippets = store.get(reviewSnippetsDataAtom);
	const filterValues = store.get(filterValuesAtom);
	const filterConfigs = store.get(filterConfigsAtom);
	const baseFilterConfigs = store.get(baseFilterConfigsAtom);
	const categoryFilterSummaries = store.get(categoryFilterSummariesAtom);
	const isAllCategoriesPage = store.get(isAllCategoriesPageAtom);
	const listingsPricingByCategory = store.get(listingsPricingByCategoryAtom);
	const classifierSsrSync = store.get(classifierSsrSyncAtom);
	return {
		featureFlags,
		reviews,
		googleReviews,
		pricing,
		detailedPricing,
		organization,
		semanticSearch,
		seoExperiment,
		contentGating,
		availability,
		storefrontsNetworkPvRec,
		awardsSummary,
		reviewPills,
		hostedBadge,
		reviewSnippets,
		filterValues,
		filterConfigs,
		baseFilterConfigs,
		categoryFilterSummaries,
		isAllCategoriesPage,
		listingsPricingByCategory,
		classifierSsrSync,
	};
};

const getReviewSnippetsInitialValues = (
	reviewSnippets: ReviewSnippetsData | null,
) => {
	return [[reviewSnippetsDataAtom, reviewSnippets]];
};

const getStorefrontJotaiInitialValues = (
	initialJotaiState: InitialJotaiState,
) => {
	const {
		featureFlags,
		reviews,
		googleReviews,
		pricing,
		detailedPricing,
		organization,
		semanticSearch,
		seoExperiment,
		contentGating,
		availability,
		storefrontsNetworkPvRec,
		awardsSummary,
		reviewPills,
		hostedBadge,
		filterValues,
		filterConfigs,
		baseFilterConfigs,
		categoryFilterSummaries,
		isAllCategoriesPage,
		listingsPricingByCategory,
		reviewSnippets,
		classifierSsrSync,
	} = initialJotaiState;
	return [
		...getReviewsInitialValues(reviews),
		...getGoogleReviewsInitialValues(googleReviews),
		...getPricingInitialValues(pricing),
		...getFeatureFlagsInitialValues(featureFlags),
		...getDetailedPricingInitialValues(detailedPricing),
		...getOrganizationInitialValues(organization),
		...getSemanticSearchInitialValues(semanticSearch),
		...getSeoExperimentInitialValues(seoExperiment),
		...getContentGatingInitialValues(contentGating),
		...getAvailabilityInitialValues(availability),
		...getStorefrontsNetworkInitialValues(storefrontsNetworkPvRec),
		...getAwardsSummaryInitialValues(awardsSummary),
		...getReviewPillsInitialValues(reviewPills),
		...getHostedBadgeInitialValues(hostedBadge),
		...getReviewSnippetsInitialValues(reviewSnippets),
		...getFilterValuesInitialValues(filterValues),
		...getFilterConfigsInitialValues(filterConfigs),
		...getBaseFilterConfigsInitialValues(baseFilterConfigs),
		...getCategoryFilterSummariesInitialValues(categoryFilterSummaries),
		...getIsAllCategoriesPageInitialValues(isAllCategoriesPage),
		...getListingsPricingByCategoryInitialValues(listingsPricingByCategory),
		...getClassifierSsrSyncInitialValues(classifierSsrSync),
	];
};

export const getJotaiInitialValues = (jotai: InitialJotaiState | undefined) => {
	if (!jotai) return [];
	return [...getStorefrontJotaiInitialValues(jotai)];
};

export const setFeatureFlags = (
	store: ReturnType<typeof createStore>,
	featureFlags: FeatureFlags,
) => {
	store.set(featureFlagsAtom, featureFlags);
};
